import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Download, Check, CreditCard, Tag, Shield, Truck, Home } from 'lucide-react';
import { Layout } from '../components/layout/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { DeliveryAddressFields } from '../components/tax/DeliveryAddressFields';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { formatPrice, getSessionId } from '../lib/utils';
import { groupCartByRoom } from '../lib/cartGroups';
import { toast } from 'sonner';
import axios from 'axios';
import React, { useState, useEffect } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export const CheckoutPage = () => {
  const { cart, clearCart, sessionId } = useCart();
  const { user, token } = useAuth();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    customer_name: user?.name || '',
    company_name: user?.company || '',
    customer_email: user?.email || '',
    customer_phone: '',
    project_address: '',
    delivery: { street: '', city: '', state: '', zip: '' },
    notes: '',
    agree_terms: false,
  });
  const [loading, setLoading] = useState(false);
  const [checkoutPreview, setCheckoutPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [priceNotice, setPriceNotice] = useState(null);

  // Browser values are sent only so the server can log differences; the server prices the stored cart itself
  const orderPayload = () => ({
    origin_url: window.location.origin,
    session_id: sessionId,
    items: cart.items.map(item => ({ id: item.id, name: item.config.room_label || getProductName(item.config.product_type), price: item.config.total_price, quantity: item.quantity })),
    client_subtotal: cart.total,
    client_total: checkoutPreview?.total,
    delivery_address: formData.delivery,
  });
  const serverLine = (id) => checkoutPreview?.lines?.find(l => l.id === id);
  const needsQuote = (id) => checkoutPreview?.requires_quote?.some(l => l.id === id);

  // Fetch checkout preview with discount on mount
  useEffect(() => {
    const fetchPreview = async () => {
      if (!cart.items?.length) return;
      
      setPreviewLoading(true);
      try {
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const response = await axios.post(`${API_URL}/checkout/preview`, {
          ...orderPayload(),
          customer_email: formData.customer_email || user?.email,
          customer_name: user?.name,
        }, { headers });
        setCheckoutPreview(response.data);
      } catch (error) {
        console.error('Failed to fetch checkout preview:', error);
      } finally {
        setPreviewLoading(false);
      }
    };
    const t = setTimeout(fetchPreview, 600);
    return () => clearTimeout(t);
  }, [cart.items, token, user, formData.delivery, formData.customer_email]); // eslint-disable-line react-hooks/exhaustive-deps

  const getProductName = (type) => {
    const names = { 'roller-shades': 'Roller Shade', 'motorized-drapes': 'Motorized Drapes', 'roman-shades': 'Roman Shade' };
    return names[type] || 'Custom Product';
  };

  const handlePayment = async () => {
    if (!formData.customer_name || !formData.customer_email) {
      toast.error('Please fill in your name and email');
      return;
    }
    if (!formData.agree_terms) {
      toast.error('Please agree to the terms and conditions');
      return;
    }

    setLoading(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.post(`${API_URL}/checkout/create-session`, {
        ...orderPayload(),
        confirmed_total: checkoutPreview?.total,
        customer_email: formData.customer_email,
        customer_name: formData.customer_name,
      }, { headers });

      // Redirect to Stripe checkout
      if (response.data.url) {
        window.location.href = response.data.url;
      } else {
        toast.error('Failed to create payment session');
      }
    } catch (error) {
      const d = error.response?.data?.detail;
      if (d?.code === 'price_changed') {
        // Never charge an amount the customer hasn't seen: show the server total and ask them to confirm again
        setCheckoutPreview(d.preview);
        setPriceNotice(d.changes || []);
        toast.warning(d.message);
      } else if (d?.code === 'requires_quote') {
        toast.error(d.message);
      } else {
        toast.error(typeof d === 'string' ? d : 'Failed to initiate payment');
      }
    } finally {
      setLoading(false);
    }
  };

  // Use preview data if available, otherwise calculate locally
  const subtotal = checkoutPreview?.subtotal || cart.total || 0;
  const discountPercentage = checkoutPreview?.discount_percentage || 0;
  const discountAmount = checkoutPreview?.discount_amount || 0;
  const discountTierName = checkoutPreview?.discount_tier_name;
  const discountedSubtotal = checkoutPreview?.discounted_subtotal || subtotal;
  const tax = checkoutPreview?.tax_amount || 0;
  const total = checkoutPreview?.total || discountedSubtotal;
  const taxPending = !checkoutPreview || checkoutPreview.tax_status === 'pending_address';
  const taxText = taxPending ? 'Tax (to be confirmed)' : checkoutPreview.tax_status === 'calculated' ? `Tax (${(checkoutPreview.tax_reason || '').replace(/^NY - /, '').replace(' (city)', '').replace(/ [\d.]+%$/, '')} ${checkoutPreview.tax_rate}%)` : checkoutPreview.tax_status === 'exempt' ? 'Tax (exempt)' : 'Tax (not collected)';

  if (!cart.items?.length) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center p-6 pt-32">
          <div className="text-center">
            <p className="text-neutral-500 mb-4">Your cart is empty</p>
            <Button onClick={() => navigate('/products')} className="btn-primary">Browse Products</Button>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="min-h-screen pt-32 pb-20" data-testid="checkout-page">
        <div className="container-xl">
          <button onClick={() => navigate(-1)} className="inline-flex items-center gap-2 text-neutral-500 hover:text-neutral-900 mb-8 transition-colors">
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Left - Customer Info */}
            <div>
              <span className="label-overline text-amber-700">Checkout</span>
              <h1 className="mt-2 mb-8">Complete Your Order</h1>
              
              <div className="space-y-6">
                {/* Contact Information */}
                <div className="bg-white border border-neutral-200 rounded-lg p-6">
                  <h3 className="font-medium mb-4">Contact Information</h3>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm text-neutral-500">Full Name *</Label>
                      <Input 
                        value={formData.customer_name} 
                        onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })} 
                        required 
                        className="mt-1" 
                        data-testid="customer-name" 
                      />
                    </div>
                    <div>
                      <Label className="text-sm text-neutral-500">Email *</Label>
                      <Input 
                        type="email" 
                        value={formData.customer_email} 
                        onChange={(e) => setFormData({ ...formData, customer_email: e.target.value })} 
                        required 
                        className="mt-1" 
                        data-testid="customer-email" 
                      />
                    </div>
                    <div>
                      <Label className="text-sm text-neutral-500">Phone</Label>
                      <Input 
                        type="tel" 
                        value={formData.customer_phone} 
                        onChange={(e) => setFormData({ ...formData, customer_phone: e.target.value })} 
                        className="mt-1" 
                      />
                    </div>
                    <div>
                      <Label className="text-sm text-neutral-500">Company</Label>
                      <Input 
                        value={formData.company_name} 
                        onChange={(e) => setFormData({ ...formData, company_name: e.target.value })} 
                        className="mt-1" 
                      />
                    </div>
                  </div>
                </div>
                
                {/* Shipping Address */}
                <div className="bg-white border border-neutral-200 rounded-lg p-6">
                  <DeliveryAddressFields value={formData.delivery} onChange={(a) => setFormData({ ...formData, delivery: a })} testId="checkout-delivery" />
                </div>
                
                {/* Order Notes */}
                <div className="bg-white border border-neutral-200 rounded-lg p-6">
                  <h3 className="font-medium mb-4">Order Notes (Optional)</h3>
                  <Textarea 
                    value={formData.notes} 
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })} 
                    placeholder="Special instructions for your order"
                    rows={3} 
                    className="mt-1" 
                  />
                </div>
                
                {/* Terms */}
                <label className="flex items-start gap-3 cursor-pointer p-4 bg-neutral-50 rounded-lg">
                  <Checkbox 
                    checked={formData.agree_terms} 
                    onCheckedChange={(checked) => setFormData({ ...formData, agree_terms: checked })} 
                    className="mt-0.5"
                    data-testid="checkout-agree-terms"
                  />
                  <span className="text-sm text-neutral-600">
                    I agree to the <a href="/terms" className="text-amber-600 hover:underline">Terms & Conditions</a> and <a href="/privacy" className="text-amber-600 hover:underline">Privacy Policy</a>. 
                    I understand that custom window treatments are made-to-order and final sale.
                  </span>
                </label>
                
                {priceNotice && (
                  <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900" data-testid="checkout-price-changed">
                    <p className="font-medium">Your total was updated to {formatPrice(total)}. Please review and press Pay again.</p>
                    {priceNotice.length > 0 && <ul className="mt-1 text-xs list-disc pl-4">
                      {priceNotice.map((c, i) => <li key={i}>{c.name || c.type}: {formatPrice(c.browser)} → {formatPrice(c.server)}</li>)}
                    </ul>}
                  </div>
                )}
                {checkoutPreview?.requires_quote?.length > 0 && (
                  <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900" data-testid="checkout-requires-quote">
                    {checkoutPreview.requires_quote.map(r => r.name).join(', ')} can't be paid online yet. <a href="/quote" className="underline font-medium">Request a quote</a> for {checkoutPreview.requires_quote.length > 1 ? 'these items' : 'this item'} or remove {checkoutPreview.requires_quote.length > 1 ? 'them' : 'it'} to pay for the rest.
                  </div>
                )}

                {/* Payment Button */}
                <Button 
                  onClick={handlePayment} 
                  disabled={loading || taxPending || !!checkoutPreview?.requires_quote?.length || !formData.agree_terms || !formData.customer_name || !formData.customer_email} 
                  className="w-full btn-primary h-14 text-base"
                  data-testid="pay-now-btn"
                >
                  {loading ? (
                    'Processing...'
                  ) : (
                    <>
                      <CreditCard className="w-5 h-5 mr-2" />
                      Pay {formatPrice(total)}
                    </>
                  )}
                </Button>
                
                {/* Trust Badges */}
                <div className="flex items-center justify-center gap-6 text-neutral-400">
                  <div className="flex items-center gap-2 text-xs">
                    <Shield className="w-4 h-4" />
                    <span>Secure Payment</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs">
                    <Truck className="w-4 h-4" />
                    <span>Free Shipping</span>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Right - Order Summary */}
            <div>
              <div className="bg-white border border-neutral-200 rounded-lg p-6 lg:p-8 sticky top-24">
                <h3 className="font-serif text-xl mb-6">Order Summary</h3>
                
                {/* Cart Items */}
                <div className="space-y-4 mb-6 max-h-72 overflow-y-auto" data-testid="checkout-items">
                  {groupCartByRoom(cart.items.map(i => ({ ...i, config: { ...i.config, total_price: needsQuote(i.id) ? 0 : (serverLine(i.id)?.line_total ?? i.config.total_price) } }))).map((group) => (
                    <div key={group.room} data-testid={`checkout-room-group-${group.room.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`}>
                      <div className="flex items-center justify-between gap-2 pb-2 mb-2 border-b border-neutral-200">
                        <p className="flex items-center gap-1.5 text-sm font-medium text-neutral-900 truncate"><Home className="w-3.5 h-3.5 text-amber-600" /> {group.room} <span className="text-xs text-neutral-400 font-normal">· {group.quantity} {group.quantity === 1 ? 'shade' : 'shades'}</span></p>
                        <p className="font-mono text-sm" data-testid="checkout-room-subtotal">{formatPrice(group.subtotal)}</p>
                      </div>
                      <div className="space-y-3">
                        {group.items.map((item) => (
                          <div key={item.id} className="flex gap-3">
                            <div className="w-12 h-12 bg-neutral-100 rounded flex items-center justify-center text-[10px] text-neutral-500 font-mono shrink-0">
                              {item.config.width}"×{item.config.height}"
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-sm truncate">{item.windowLabel || getProductName(item.config.product_type)}</p>
                              <p className="text-xs text-neutral-500">{getProductName(item.config.product_type)} · Qty {item.quantity}</p>
                            </div>
                            {needsQuote(item.id) ? <p className="text-xs font-medium text-amber-700" data-testid={`checkout-requires-quote-${item.id}`}>Requires quote</p> : <p className="font-mono text-sm" data-testid={`checkout-line-${item.id}`}>{formatPrice(serverLine(item.id)?.line_total ?? item.config.total_price)}</p>}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
                
                {/* Pricing Breakdown */}
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-neutral-500">Subtotal</span>
                    <span className="font-mono">{formatPrice(subtotal)}</span>
                  </div>
                  
                  {/* Dealer Discount */}
                  {discountPercentage > 0 && (
                    <div className="flex justify-between text-green-600">
                      <span className="flex items-center gap-2">
                        <Tag className="w-4 h-4" />
                        {discountTierName} Discount ({discountPercentage}%)
                      </span>
                      <span className="font-mono">-{formatPrice(discountAmount)}</span>
                    </div>
                  )}
                  
                  {discountPercentage > 0 && (
                    <div className="flex justify-between">
                      <span className="text-neutral-500">After Discount</span>
                      <span className="font-mono">{formatPrice(discountedSubtotal)}</span>
                    </div>
                  )}
                  
                  <div className="flex justify-between">
                    <span className="text-neutral-500" data-testid="checkout-tax-label">{taxText}</span>
                    <span className="font-mono" data-testid="checkout-tax">{taxPending ? 'TBC' : formatPrice(tax)}</span>
                  </div>
                  {checkoutPreview?.tax_reason && <p className="text-[11px] text-neutral-400 text-right" data-testid="checkout-tax-reason">{checkoutPreview.tax_reason}</p>}
                  <div className="hidden">
                  </div>
                  
                  <div className="flex justify-between text-lg font-medium pt-3 border-t border-neutral-200">
                    <span>Total</span>
                    <span className="font-mono" data-testid="checkout-total">{formatPrice(total)}</span>
                  </div>
                </div>
                
                {/* Dealer Badge */}
                {checkoutPreview?.is_dealer && discountPercentage > 0 && (
                  <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                    <div className="flex items-center gap-2 text-amber-800">
                      <Tag className="w-5 h-5" />
                      <span className="font-medium">Trade Program Member</span>
                    </div>
                    <p className="text-sm text-amber-600 mt-1">
                      Your {discountTierName} tier discount has been applied.
                    </p>
                  </div>
                )}
                
                {/* Not a dealer yet? */}
                {!checkoutPreview?.is_dealer && (
                  <div className="mt-6 p-4 bg-neutral-50 rounded-lg">
                    <p className="text-sm text-neutral-600">
                      Are you a designer, architect, or contractor?{' '}
                      <a href="/trade" className="text-amber-600 hover:underline font-medium">
                        Join our Trade Program
                      </a>{' '}
                      to unlock exclusive discounts.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};
